import { useState } from "react";
import { BadgeCheck, ChevronDown, ChevronUp, Play, Plus, Trash2 } from "lucide-react";
import { Button } from "@omnitok/ui";

import { useT } from "@/i18n";
import { api } from "@/lib/api";
import { Field } from "@/pages/builder/components/Field";
import { useTrainingRows } from "@/pages/builder/ai/useTrainingRows";
import type { UseBuilderDraft } from "@/hooks/useBuilderDraft";
import type { AiHomeReference } from "@/types";

/**
 * The report's reference elements for the AI Home (2026-10-23): its
 * official KPIs and charts. When a request asks for one, the AI Home uses
 * this SQL as written instead of writing one, and its AI review never
 * rewrites it. Time goes through the time rule's placeholders; dashboard
 * filters reach it only on the columns marked ``{{filter:<column>}}``.
 *
 * "Probar" runs it in the portal on the latest period (the portal owns the
 * time rule and the sandbox); publishing runs every reference and a
 * broken one blocks it. Mirrors ``hp_portal.services.ai_home.spec.Reference``.
 */

const CHART_TYPES = [
  "bar",
  "bar_horizontal",
  "line",
  "pie",
  "grouped_bar",
  "stacked_bar",
  "multi_line",
  "stacked_area",
  "heatmap",
  "table",
] as const;
const FORMATS = ["number", "percent", "currency"] as const;

interface TestOut {
  result: { kind: "kpi" | "chart"; data?: { value?: number | null; data?: unknown[] }; error?: string };
  filters: string[];
}

type Tried =
  | { state: "running" }
  | { state: "ok"; summary: string; filters: string[] }
  | { state: "error"; message: string };

function newId(taken: Set<string>): string {
  for (let n = 1; ; n += 1) if (!taken.has(`ref_${n}`)) return `ref_${n}`;
}

export function AiHomeReferenceSection({ draft }: { draft: UseBuilderDraft }): JSX.Element {
  const { t } = useT();
  const refs = draft.ai.ai_home?.reference ?? [];
  const [open, setOpen] = useState<string | null>(null);
  const [tried, setTried] = useState<Record<string, Tried>>({});
  const [picking, setPicking] = useState(false);
  const examples = useTrainingRows(draft.key, "example");

  function save(next: AiHomeReference[]): void {
    draft.setAI({ ai_home: { ...draft.ai.ai_home, reference: next } });
  }

  function add(from: Partial<AiHomeReference>): void {
    const id = newId(new Set(refs.map((r) => r.id)));
    save([...refs, { id, title: "", kind: "kpi", chart_type: null, format: "number", sql: "", scope_safe: false, ...from }]);
    setOpen(id);
  }

  function patch(id: string, p: Partial<AiHomeReference>): void {
    save(refs.map((r) => (r.id === id ? { ...r, ...p } : r)));
    // A changed reference has not been tried as it is now.
    setTried((prev) => {
      const { [id]: _gone, ...rest } = prev;
      return rest;
    });
  }

  async function tryIt(ref: AiHomeReference): Promise<void> {
    setTried((prev) => ({ ...prev, [ref.id]: { state: "running" } }));
    try {
      const out = await api.post<TestOut>(`/api/builder/${draft.key}/ai-home/reference/test`, {
        time: draft.ai.ai_home?.time ?? {},
        reference: ref,
      });
      const r = out.result;
      if (r.error !== undefined) {
        setTried((prev) => ({ ...prev, [ref.id]: { state: "error", message: r.error ?? "" } }));
        return;
      }
      const value = r.data?.value;
      const summary =
        r.kind === "kpi"
          ? value === null || value === undefined
            ? "—"
            : ref.format === "percent"
              ? `${(value * 100).toLocaleString(undefined, { maximumFractionDigits: 1 })} %`
              : value.toLocaleString()
          : t("editor.ai.aiHomeRef.rows", { count: r.data?.data?.length ?? 0 });
      setTried((prev) => ({ ...prev, [ref.id]: { state: "ok", summary, filters: out.filters } }));
    } catch (e: unknown) {
      setTried((prev) => ({ ...prev, [ref.id]: { state: "error", message: e instanceof Error ? e.message : String(e) } }));
    }
  }

  const sqlExamples = examples.rows.filter((row) => row.content.trim() !== "");

  return (
    <section className="rounded-[14px] border border-neutral-200 bg-white shadow-card p-5" data-testid="ai-home-references">
      <header className="mb-2 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 text-base font-semibold text-neutral-900">
            <BadgeCheck className="h-4 w-4 text-primary-dark" aria-hidden="true" />
            {t("editor.ai.aiHomeRef.title")}
          </h2>
          <p className="mt-1 max-w-3xl text-xs text-neutral-500">{t("editor.ai.aiHomeRef.description")}</p>
          <p className="mt-1 max-w-3xl text-xs text-neutral-500">
            {t("editor.ai.aiHomeRef.placeholders", {
              asOf: `{{as_of:${draft.key}}}`,
              since: `{{since:${draft.key}}}`,
              filter: "{{filter:<column>}}",
            })}
          </p>
        </div>
        <div className="relative flex shrink-0 gap-2">
          <Button type="button" variant="secondary" size="sm" onClick={() => setPicking((v) => !v)} aria-expanded={picking}>
            {t("editor.ai.aiHomeRef.fromExample")}
          </Button>
          <Button type="button" size="sm" data-testid="ai-home-ref-add" leftIcon={<Plus className="h-4 w-4" aria-hidden="true" />} onClick={() => add({})}>
            {t("editor.ai.aiHomeRef.add")}
          </Button>
          {picking && (
            <div role="menu" className="absolute right-0 top-11 z-20 w-80 rounded-lg border border-neutral-200 bg-white p-2 shadow-lg">
              {sqlExamples.length === 0 ? (
                <p className="p-2 text-xs text-neutral-500">{t("editor.ai.aiHomeRef.noExamples")}</p>
              ) : (
                sqlExamples.map((row) => (
                  <button
                    key={row.id}
                    type="button"
                    role="menuitem"
                    className="block w-full truncate rounded px-2 py-1.5 text-left text-sm hover:bg-neutral-100"
                    onClick={() => {
                      setPicking(false);
                      add({ title: row.name.slice(0, 120), sql: row.content });
                    }}
                  >
                    {row.name}
                  </button>
                ))
              )}
              <p className="p-2 text-xs text-neutral-500">{t("editor.ai.aiHomeRef.fromExampleHint")}</p>
            </div>
          )}
        </div>
      </header>

      {refs.length === 0 ? (
        <p className="rounded-md border border-dashed border-neutral-200 px-3 py-4 text-center text-xs text-neutral-500">
          {t("editor.ai.aiHomeRef.empty")}
        </p>
      ) : (
        <ul className="mt-3 space-y-2">
          {refs.map((ref) => {
            const state = tried[ref.id];
            const expanded = open === ref.id;
            return (
              <li key={ref.id} data-testid={`ai-home-ref-${ref.id}`} className="rounded-lg border border-neutral-200 px-3 py-2.5">
                <button
                  type="button"
                  className="flex w-full items-center gap-3 text-left"
                  aria-expanded={expanded}
                  onClick={() => setOpen(expanded ? null : ref.id)}
                >
                  <span className="rounded bg-primary-soft px-2 py-0.5 text-xs font-semibold text-primary-ink">
                    {ref.kind === "kpi" ? "KPI" : ref.chart_type}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-neutral-900">
                    {ref.title || t("editor.ai.aiHomeRef.untitled")}
                  </span>
                  {state?.state === "ok" && (
                    <span className="text-xs font-semibold text-neutral-800">
                      ✓ {t("editor.ai.aiHomeRef.runs", { value: state.summary })}
                    </span>
                  )}
                  {state?.state === "error" && (
                    <span className="text-xs font-semibold text-error-dark">✕ {t("editor.ai.aiHomeRef.fails")}</span>
                  )}
                  {expanded ? <ChevronUp className="h-4 w-4" aria-hidden="true" /> : <ChevronDown className="h-4 w-4" aria-hidden="true" />}
                </button>
                {expanded && (
                  <div className="mt-3 space-y-3">
                    <div className="grid gap-3 md:grid-cols-[1fr_180px_180px]">
                      <Field id={`ref-title-${ref.id}`} label={t("editor.ai.aiHomeRef.titleLabel")}>
                        <input
                          id={`ref-title-${ref.id}`}
                          className="block w-full rounded-[10px] border border-control bg-white px-3 py-2 text-sm placeholder:text-neutral-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary"
                          maxLength={120}
                          value={ref.title}
                          onChange={(e) => patch(ref.id, { title: e.target.value })}
                        />
                      </Field>
                      <Field id={`ref-kind-${ref.id}`} label={t("editor.ai.aiHomeRef.kindLabel")}>
                        <select
                          id={`ref-kind-${ref.id}`}
                          className="block w-full rounded-[10px] border border-control bg-white px-3 py-2 text-sm placeholder:text-neutral-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary"
                          value={ref.kind === "kpi" ? "kpi" : (ref.chart_type ?? "bar")}
                          onChange={(e) =>
                            e.target.value === "kpi"
                              ? patch(ref.id, { kind: "kpi", chart_type: null })
                              : patch(ref.id, { kind: "chart", chart_type: e.target.value })
                          }
                        >
                          <option value="kpi">KPI</option>
                          {CHART_TYPES.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Field id={`ref-format-${ref.id}`} label={t("editor.ai.aiHomeRef.formatLabel")}>
                        <select
                          id={`ref-format-${ref.id}`}
                          className="block w-full rounded-[10px] border border-control bg-white px-3 py-2 text-sm placeholder:text-neutral-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary"
                          value={ref.format}
                          onChange={(e) => patch(ref.id, { format: e.target.value as AiHomeReference["format"] })}
                        >
                          {FORMATS.map((f) => (
                            <option key={f} value={f}>
                              {t(`editor.ai.aiHomeRef.format.${f}`)}
                            </option>
                          ))}
                        </select>
                      </Field>
                    </div>
                    <Field id={`ref-sql-${ref.id}`} label="SQL">
                      <textarea
                        id={`ref-sql-${ref.id}`}
                        className="block w-full rounded-[10px] border border-control bg-white px-3 py-2 placeholder:text-neutral-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary min-h-[140px] font-mono text-xs"
                        maxLength={4000}
                        spellCheck={false}
                        value={ref.sql}
                        onChange={(e) => patch(ref.id, { sql: e.target.value })}
                      />
                    </Field>
                    <label className="flex items-center gap-2 text-sm text-neutral-800">
                      <input
                        type="checkbox"
                        checked={ref.scope_safe}
                        onChange={(e) => patch(ref.id, { scope_safe: e.target.checked })}
                      />
                      {t("editor.ai.aiHomeRef.scopeSafe")}
                    </label>
                    <div className="flex flex-wrap items-center gap-3">
                      <Button
                        type="button"
                        size="sm"
                        data-testid={`ai-home-ref-test-${ref.id}`}
                        disabled={state?.state === "running" || !ref.sql.trim() || !ref.title.trim()}
                        leftIcon={<Play className="h-4 w-4" aria-hidden="true" />}
                        onClick={() => void tryIt(ref)}
                      >
                        {state?.state === "running" ? t("editor.ai.aiHomeRef.testing") : t("editor.ai.aiHomeRef.test")}
                      </Button>
                      {state?.state === "ok" && (
                        <span className="text-sm text-neutral-800" data-testid={`ai-home-ref-result-${ref.id}`}>
                          {t("editor.ai.aiHomeRef.runs", { value: state.summary })} ·{" "}
                          {state.filters.length > 0
                            ? t("editor.ai.aiHomeRef.filters", { columns: state.filters.join(", ") })
                            : t("editor.ai.aiHomeRef.noFilters")}
                        </span>
                      )}
                      {state?.state === "error" && (
                        <span role="alert" className="text-sm text-error-dark" data-testid={`ai-home-ref-error-${ref.id}`}>
                          {state.message}
                        </span>
                      )}
                      <button
                        type="button"
                        className="ml-auto inline-flex items-center gap-1 text-sm text-neutral-700 hover:text-error-dark"
                        onClick={() => save(refs.filter((r) => r.id !== ref.id))}
                      >
                        <Trash2 className="h-4 w-4" aria-hidden="true" /> {t("editor.ai.aiHomeRef.remove")}
                      </button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <p className="mt-3 text-xs text-neutral-500">{t("editor.ai.aiHomeRef.publishNote")}</p>
    </section>
  );
}
